import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import CloudflareLinkModal, { CloudflareAlias } from './components/CloudflareLinkModal';

interface StaffEntry {
  user_id: string;
  nom: string;
  prenom: string;
  email: string | null;
  email_2fa: string | null;
  table: string;
  role: string;
  icon: string;
}

const STAFF_TABLES: { table: string; role: string; icon: string; hasEmail: boolean }[] = [
  { table: 'medecins', role: 'Médecin', icon: 'ri-stethoscope-line', hasEmail: true },
  { table: 'infirmiers', role: 'Infirmier', icon: 'ri-empathize-line', hasEmail: true },
  { table: 'sage_femmes', role: 'Sage-femme', icon: 'ri-nurse-line', hasEmail: true },
  { table: 'secretaires', role: 'Secrétaire', icon: 'ri-user-settings-line', hasEmail: true },
  { table: 'laborantins', role: 'Laborantin', icon: 'ri-microscope-line', hasEmail: true },
  { table: 'comptables', role: 'Comptable', icon: 'ri-calculator-line', hasEmail: true },
  { table: 'super_admins', role: 'Administrateur', icon: 'ri-shield-user-line', hasEmail: false },
];

export default function Admin2faPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [accessGranted, setAccessGranted] = useState(false);
  const [staff, setStaff] = useState<StaffEntry[]>([]);
  const [search, setSearch] = useState('');
  const [require2fa, setRequire2fa] = useState(false);
  const [savingToggle, setSavingToggle] = useState(false);
  const [savingEntry, setSavingEntry] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [cloudflareAliases, setCloudflareAliases] = useState<CloudflareAlias[]>([]);
  const [cloudflareLoading, setCloudflareLoading] = useState(false);
  const [cloudflareStatus, setCloudflareStatus] = useState<'idle' | 'ok' | 'error'>('idle');
  const [cloudflareError, setCloudflareError] = useState('');
  const [linkModalEntry, setLinkModalEntry] = useState<StaffEntry | null>(null);

  useEffect(() => {
    checkAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const checkAccess = async () => {
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }
    const { data, error } = await supabase
      .from('super_admins')
      .select('id')
      .eq('user_id', user.id)
      .single();
    if (error || !data) {
      navigate('/', { replace: true });
      return;
    }
    setAccessGranted(true);
    setLoading(false);
    loadStaff();
    loadSecurityConfig();
    loadCloudflareAliases();
  };

  const loadSecurityConfig = async () => {
    try {
      const { data } = await supabase
        .from('system_config')
        .select('config_value')
        .eq('config_key', 'security')
        .single();
      if (data?.config_value?.require2faForStaff === true) {
        setRequire2fa(true);
      }
    } catch {
      // Silencieux
    }
  };

  const loadStaff = async () => {
    const all: StaffEntry[] = [];
    for (const t of STAFF_TABLES) {
      try {
        const cols = t.hasEmail
          ? 'user_id, nom, prenom, email, email_2fa'
          : 'user_id, nom, prenom, email_2fa';
        const { data } = await supabase.from(t.table).select(cols);
        if (data) {
          for (const row of data) {
            all.push({
              user_id: row.user_id,
              nom: row.nom || '',
              prenom: row.prenom || '',
              email: row.email || null,
              email_2fa: row.email_2fa || null,
              table: t.table,
              role: t.role,
              icon: t.icon,
            });
          }
        }
      } catch {
        // Silencieux
      }
    }
    all.sort((a, b) => a.role.localeCompare(b.role) || a.nom.localeCompare(b.nom));
    setStaff(all);
  };

  const handleToggle2fa = async () => {
    setSavingToggle(true);
    setMessage('');
    setErrorMsg('');
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) throw new Error('Non authentifié');

      const { data: sc } = await supabase
        .from('system_config')
        .select('config_value')
        .eq('config_key', 'security')
        .single();
      const current = sc?.config_value || {};

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/update-system-config`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            configType: 'security',
            configData: { ...current, require2faForStaff: !require2fa },
          }),
        }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Erreur lors de la sauvegarde');

      setRequire2fa(!require2fa);
      setMessage(
        !require2fa
          ? 'La 2FA est désormais obligatoire pour tout le personnel sans email 2FA.'
          : 'La 2FA obligatoire a été désactivée.'
      );
      setTimeout(() => setMessage(''), 4000);
    } catch (e: any) {
      setErrorMsg(e.message || 'Erreur lors de la sauvegarde');
      setTimeout(() => setErrorMsg(''), 4000);
    } finally {
      setSavingToggle(false);
    }
  };

  const startEdit = (entry: StaffEntry) => {
    setEditingId(entry.user_id);
    setEditValue(entry.email_2fa || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue('');
  };

  const saveEdit = async (entry: StaffEntry) => {
    setSavingEntry(entry.user_id);
    setMessage('');
    setErrorMsg('');
    const newEmail = editValue.trim();

    if (newEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      setErrorMsg('Adresse email invalide. Veuillez saisir un email valide.');
      setTimeout(() => setErrorMsg(''), 4000);
      setSavingEntry(null);
      return;
    }

    try {
      const { error } = await supabase
        .from(entry.table)
        .update({ email_2fa: newEmail || null })
        .eq('user_id', entry.user_id);
      if (error) throw new Error(error.message);

      setStaff((prev) =>
        prev.map((s) =>
          s.user_id === entry.user_id ? { ...s, email_2fa: newEmail || null } : s
        )
      );
      setEditingId(null);
      setEditValue('');
      setMessage(newEmail ? 'Email 2FA enregistré avec succès.' : 'Email 2FA retiré.');
      setTimeout(() => setMessage(''), 3000);
    } catch (e: any) {
      setErrorMsg(e.message || 'Erreur lors de la sauvegarde');
      setTimeout(() => setErrorMsg(''), 4000);
    } finally {
      setSavingEntry(null);
    }
  };

  const loadCloudflareAliases = async () => {
    setCloudflareLoading(true);
    setCloudflareError('');
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) throw new Error('Non authentifié');
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/manage-cloudflare-email-routing`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ action: 'list' }),
        }
      );
      const data = await response.json();
      if (data.status === 'ok') {
        setCloudflareAliases(data.aliases || []);
        setCloudflareStatus('ok');
      } else {
        setCloudflareStatus('error');
        const parts: string[] = [];
        if (data.reason) parts.push(data.reason);
        if (data.detail) parts.push(`Cloudflare : ${data.detail}`);
        if (data.hint) parts.push(data.hint);
        setCloudflareError(parts.join(' ') || 'Connexion Cloudflare impossible.');
      }
    } catch (e: any) {
      setCloudflareStatus('error');
      setCloudflareError(e.message || 'Erreur réseau');
    } finally {
      setCloudflareLoading(false);
    }
  };

  const handleLinked = async (entry: StaffEntry, alias: string) => {
    setMessage('');
    setErrorMsg('');
    try {
      const { error } = await supabase
        .from(entry.table)
        .update({ email_2fa: alias })
        .eq('user_id', entry.user_id);
      if (error) throw new Error(error.message);
      setStaff((prev) =>
        prev.map((s) => (s.user_id === entry.user_id ? { ...s, email_2fa: alias } : s))
      );
      setMessage('Email 2FA lié avec succès.');
      setTimeout(() => setMessage(''), 3000);
    } catch (e: any) {
      setErrorMsg(e.message || 'Erreur lors de la sauvegarde');
      setTimeout(() => setErrorMsg(''), 4000);
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return staff;
    const t = search.trim().toLowerCase();
    return staff.filter(
      (s) =>
        `${s.prenom} ${s.nom}`.toLowerCase().includes(t) ||
        (s.email_2fa || '').toLowerCase().includes(t) ||
        (s.email || '').toLowerCase().includes(t) ||
        s.role.toLowerCase().includes(t)
    );
  }, [staff, search]);

  const configured = staff.filter((s) => s.email_2fa).length;
  const missing = staff.length - configured;

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-clinic-blue-dark text-xl">Chargement...</div>
      </div>
    );
  }

  if (!accessGranted) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-clinic-blue-dark">
                Gestion 2FA du personnel
              </h1>
              <p className="text-gray-600 mt-2 text-sm sm:text-base">
                Associez un email (alias du domaine) à chaque membre pour recevoir les codes de vérification.
              </p>
            </div>
            <button
              onClick={() => navigate('/admin')}
              className="px-4 py-2 text-clinic-blue hover:text-clinic-blue-dark transition-colors whitespace-nowrap text-sm sm:text-base cursor-pointer"
            >
              ← Retour au tableau de bord
            </button>
          </div>
        </div>

        {message && (
          <div className="mb-6 p-4 bg-green-50 border-2 border-green-200 text-green-600 rounded-2xl flex items-start text-sm">
            <i className="ri-check-line text-xl mr-2 mt-0.5 flex-shrink-0"></i>
            <span>{message}</span>
          </div>
        )}

        {errorMsg && (
          <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 text-red-600 rounded-2xl flex items-start text-sm">
            <i className="ri-error-warning-line text-xl mr-2 mt-0.5 flex-shrink-0"></i>
            <span>{errorMsg}</span>
          </div>
        )}

        {/* Interrupteur 2FA obligatoire */}
        <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 p-4 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-start gap-3 flex-1">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="ri-shield-flash-line text-xl text-amber-600"></i>
              </div>
              <div>
                <h2 className="font-bold text-clinic-blue-dark text-base mb-1">
                  Exiger l'email 2FA pour tout le personnel
                </h2>
                <p className="text-sm text-gray-600">
                  Quand cette option est active, tout membre du personnel qui n'a pas encore d'email 2FA
                  sera <strong>bloqué à la connexion</strong> jusqu'à ce qu'un alias lui soit associé.
                </p>
                <p className="text-xs text-gray-500 mt-2 flex items-start gap-1.5">
                  <i className="ri-information-line mt-0.5 flex-shrink-0"></i>
                  <span>
                    L'email 2FA est l'alias du domaine (ex : prenom.nom@cliniquesaintefamille.com) que vous
                    avez configuré dans Cloudflare pour rediriger vers l'email personnel du membre.
                  </span>
                </p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
              <input
                type="checkbox"
                checked={require2fa}
                onChange={handleToggle2fa}
                disabled={savingToggle}
                className="sr-only peer"
              />
              <div
                className={`w-12 h-7 rounded-full peer peer-checked:after:translate-x-5 rtl:peer-checked:after:-translate-x-5 after:content-[''] after:absolute after:top-[3px] after:start-[3px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-[22px] after:w-[22px] after:transition-all ${
                  require2fa ? 'bg-clinic-green' : 'bg-gray-300'
                }`}
              ></div>
            </label>
          </div>
        </div>

        {/* Connexion Cloudflare */}
        <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 p-4 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-start gap-3 flex-1">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="ri-cloud-line text-xl text-orange-600"></i>
              </div>
              <div>
                <h2 className="font-bold text-clinic-blue-dark text-base mb-1">
                  Connexion Cloudflare (création d'alias automatique)
                </h2>
                <p className="text-sm text-gray-600">
                  Au lieu de créer chaque alias à la main dans Cloudflare, saisissez l'email personnel d'un membre et
                  l'app crée automatiquement l'alias <strong>prenom.nom@cliniquesaintefamille.com</strong> + la redirection.
                </p>
                {cloudflareStatus === 'ok' && (
                  <p className="text-xs text-green-600 mt-2 flex items-center gap-1.5">
                    <i className="ri-checkbox-circle-line"></i>
                    <span>{cloudflareAliases.length} alias détecté(s) dans Cloudflare.</span>
                  </p>
                )}
                {cloudflareStatus === 'error' && (
                  <p className="text-xs text-red-600 mt-2 flex items-start gap-1.5">
                    <i className="ri-error-warning-line mt-0.5 flex-shrink-0"></i>
                    <span>{cloudflareError}</span>
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={() => loadCloudflareAliases()}
              disabled={cloudflareLoading}
              className="px-4 py-2 bg-white border-2 border-orange-200 text-orange-600 rounded-lg hover:bg-orange-50 transition-colors whitespace-nowrap text-sm flex items-center gap-2 cursor-pointer disabled:opacity-50"
            >
              {cloudflareLoading ? (
                <i className="ri-loader-4-line animate-spin"></i>
              ) : (
                <i className="ri-refresh-line"></i>
              )}
              Rafraîchir les alias
            </button>
          </div>
        </div>

        {/* Statistiques */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-2xl border-2 border-gray-100 p-5">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 bg-clinic-blue/10 rounded-xl flex items-center justify-center">
                <i className="ri-team-line text-lg text-clinic-blue"></i>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{staff.length}</div>
                <div className="text-xs text-gray-500">Membres du personnel</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border-2 border-gray-100 p-5">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 bg-green-100 rounded-xl flex items-center justify-center">
                <i className="ri-shield-check-line text-lg text-green-600"></i>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600">{configured}</div>
                <div className="text-xs text-gray-500">2FA configurée</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border-2 border-gray-100 p-5">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 bg-amber-100 rounded-xl flex items-center justify-center">
                <i className="ri-alert-line text-lg text-amber-600"></i>
              </div>
              <div>
                <div className="text-2xl font-bold text-amber-600">{missing}</div>
                <div className="text-xs text-gray-500">Sans email 2FA</div>
              </div>
            </div>
          </div>
        </div>

        {/* Recherche */}
        <div className="mb-4">
          <div className="relative">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher par nom, rôle ou email..."
              className="w-full pl-10 pr-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:outline-none focus:border-clinic-blue/40 transition-colors text-sm"
            />
            <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"></i>
          </div>
        </div>

        {/* Liste du personnel */}
        <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 overflow-hidden">
          <div className="px-4 sm:px-6 py-4 border-b-2 border-gray-100 flex items-center justify-between">
            <h2 className="font-bold text-clinic-blue-dark text-base">
              Personnel ({filtered.length})
            </h2>
            <span className="text-xs text-gray-500">Cliquez sur l'icône crayon pour modifier l'email 2FA</span>
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <i className="ri-user-search-line text-4xl block mb-2"></i>
              <p className="text-sm">Aucun membre trouvé</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filtered.map((entry) => {
                const isEditing = editingId === entry.user_id;
                const isMissing = !entry.email_2fa;
                return (
                  <div
                    key={`${entry.table}-${entry.user_id}`}
                    className="px-4 sm:px-6 py-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-10 h-10 bg-clinic-blue/10 rounded-xl flex items-center justify-center flex-shrink-0">
                          <i className={`${entry.icon} text-clinic-blue`}></i>
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold text-gray-900 text-sm whitespace-nowrap">
                              {entry.prenom} {entry.nom}
                            </p>
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                              {entry.role}
                            </span>
                          </div>
                          {entry.email && (
                            <p className="text-xs text-gray-500 truncate">
                              Connexion : {entry.email}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 sm:w-[360px] flex-shrink-0">
                        {isEditing ? (
                          <>
                            <input
                              type="email"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              placeholder="prenom.nom@clinique.com"
                              autoFocus
                              className="flex-1 px-3 py-2 border-2 border-clinic-blue/40 rounded-lg focus:outline-none focus:border-clinic-blue text-sm"
                            />
                            <button
                              onClick={() => saveEdit(entry)}
                              disabled={savingEntry === entry.user_id}
                              className="w-9 h-9 bg-clinic-green text-white rounded-lg hover:bg-clinic-green-dark transition-colors flex items-center justify-center cursor-pointer disabled:opacity-50"
                              title="Enregistrer"
                            >
                              {savingEntry === entry.user_id ? (
                                <i className="ri-loader-4-line animate-spin"></i>
                              ) : (
                                <i className="ri-check-line"></i>
                              )}
                            </button>
                            <button
                              onClick={cancelEdit}
                              className="w-9 h-9 bg-gray-200 text-gray-600 rounded-lg hover:bg-gray-300 transition-colors flex items-center justify-center cursor-pointer"
                              title="Annuler"
                            >
                              <i className="ri-close-line"></i>
                            </button>
                          </>
                        ) : (
                          <>
                            <div className="flex-1 flex items-center gap-2 min-w-0">
                              {entry.email_2fa ? (
                                <span className="text-sm text-gray-700 truncate flex items-center gap-1.5">
                                  <i className="ri-mail-check-line text-green-600 flex-shrink-0"></i>
                                  <span className="truncate">{entry.email_2fa}</span>
                                </span>
                              ) : (
                                <span className="text-sm text-amber-600 flex items-center gap-1.5">
                                  <i className="ri-alert-line flex-shrink-0"></i>
                                  <span className="truncate">Aucun email 2FA</span>
                                </span>
                              )}
                            </div>
                            <button
                              onClick={() => setLinkModalEntry(entry)}
                              className="w-9 h-9 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors flex items-center justify-center cursor-pointer"
                              title="Créer / lier un alias Cloudflare"
                            >
                              <i className="ri-cloud-line"></i>
                            </button>
                            <button
                              onClick={() => startEdit(entry)}
                              className="w-9 h-9 text-clinic-blue hover:bg-clinic-blue/10 rounded-lg transition-colors flex items-center justify-center cursor-pointer"
                              title="Modifier l'email 2FA"
                            >
                              <i className="ri-pencil-line"></i>
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {linkModalEntry && (
        <CloudflareLinkModal
          entry={linkModalEntry}
          aliases={cloudflareAliases}
          onClose={() => setLinkModalEntry(null)}
          onLinked={(alias) => handleLinked(linkModalEntry, alias)}
        />
      )}

      <Footer />
    </div>
  );
}